import React, { useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  PriceEvolutionChart,
  PriceEvolutionByCategory,
} from '../../../PriceEvolutionChart'
import { formatPriceM2EvolutionPrice } from '../../../PriceM2EvolutionChart'

import { usePurchaseMarket } from '../../PurchaseMarketContext'

interface PriceM2EvolutionProps {
  t: TFunction
}

export const PriceM2Evolution: React.FC<PriceM2EvolutionProps> = ({ t }) => {
  const { filters, service } = usePurchaseMarket()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<PriceEvolutionByCategory>({})
  const [currentView, setCurrentView] = useState<
    'absolute' | 'index' | 'growth' | 'quantile'
  >('absolute')

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Determine location type and value from filters
        let locationType = undefined
        let locationValue = undefined

        if (filters.location) {
          switch (filters.locationType) {
            case 'postal_code':
              locationType = 'postal_code_name'
              break
            case 'city':
              locationType = 'municipality_name'
              break
            case 'district':
              locationType = 'district_name'
              break
          }
          locationValue = filters.location
        }

        const apiFilters = {
          deal_type: 'purchase',
          property_category:
            filters.propertyCategory.length > 0
              ? filters.propertyCategory
              : undefined,
          location: locationValue,
          location_type: locationType,
          price_field: 'price_per_square_meter',
          outlier_field: 'price_per_square_meter',
          outlier_percentage: 2,
        }

        const response = await service.getPriceEvolutionData(apiFilters)

        // Pass the raw aggregations data directly to the component
        setData(response.aggregations)
      } catch (err) {
        console.error('Error fetching price/m² evolution data:', err)
        setError(t('market.common.errors.loadingData'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [filters])

  return (
    <PriceEvolutionChart
      data={data}
      loading={loading}
      error={error}
      currentView={currentView}
      onViewChange={setCurrentView}
      isRental={false}
      isSqm={true}
      title={t('purchase.sections.priceM2Evolution.title')}
      formatValue={formatPriceM2EvolutionPrice}
      yAxisFormatter={(value: number) => `CHF ${value.toLocaleString()}/m²`}
      tooltipFormatter={formatPriceM2EvolutionPrice}
      showKPIs={false}
      showInsights={false}
      showQuantileChart={false}
      t={t}
    />
  )
}
